import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Award, TrendingUp, MapPin, Building2 } from 'lucide-react';
import { ScrollProgress } from '../components/FloatingShapes';
import { FadeInOnScroll, StaggerChildren } from '../components/ParallaxEffects';
import { ParallaxShapes } from '../components/ParallaxShapes';
import { useSEO } from '../hooks/useSEO';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {prefix}{count}{suffix}
    </span>
  );
};

export const About = () => {
  const [scrollY, setScrollY] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamMembers = [
    {
      id: 1,
      name: 'Christoph Jermann',
      position: 'Immobilienökonom MAS FHO',
      subtitle: 'eidg. Dipl. Betriebswirtschafter',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/yprdkmz4_Christoph%20Jermann_Hintergrund%20transparent.webp',
      bio: [
        'Christoph Jermann studierte Betriebswirtschaft an der Akademie St. Gallen und bildete sich im Rahmen Master of Advanced Studies in Real Estate Management zum Immobilienökonomen mit Fokus auf die Immobilienentwicklung weiter.',
        'Nach abgeschlossener Ausbildung bei einer Grossbank war er für eine Regionalbank langjährig für die Betreuung von Firmenkunden zuständig. In sein Aufgabengebiet fielen dabei die Finanzierung von Investitions- und Produktionsgütern, Strukturierung von Kreditlinien, wie auch Unternehmenstransaktionen (Management-Buy-In oder -Buy-Out).',
        'Im November 2021 hat Christoph Jermann sich mit dem Fokus auf die Umsetzung von Immobilienentwicklungen verselbständigt und entwickelt einerseits für den Eigenbestand, jedoch auch im Auftrag Immobilienprojekte weiter.',
        'Mit der Übernahme der Mehrheitsbeteiligung an der Schweizer Nachfolge im August 2022 wurde Christoph Jermann als Senior Client Advisor im Bereich Mergers & Acquisitions tätig und strukturiert und ermöglicht Unternehmenstransaktionen im DACH-Raum.'
      ],
      externalLink: 'https://linktr.ee/cj_jermann'
    },
    {
      id: 2,
      name: 'Michael Kummer',
      position: 'Rechtsanwalt, Notar',
      subtitle: 'lic. iur. HSG, LL.M., M.B.L.-HSG',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/ocbk3ks8_Mic%20Kummer%20Hintergrund%20transparent.webp',
      bio: [
        'Michael Kummer studierte Rechtswissenschaften an der Universität St.Gallen und schloss sein Studium Anfang 2005 ab. Bereits während seines Studiums war er Mitgründer eines Jungunternehmens mit Kernkompetenzen in den Bereichen Business Intelligence, e-Commerce und Internet-Applikationen. Im Jahr 2007 erwarb er das st.gallische Anwaltspatent. Michael Kummer ist derzeit Partner bei Stach Rechtsanwälte AG.',
        'Seit 2009 ist Michael Kummer Mentor an der Universität St.Gallen und seit Januar 2011 Dozent an der GET Kaderschule in Winterthur (Arbeitsrecht, Kommunikationsrecht).',
        'Im November 2011 hat er seinen Master of European and International Business Law an der Universität St.Gallen (M.B.L.-HSG) und im September 2014 seinen Master in Internationalem Wirtschaftsrecht an der Universität Zürich mit der Vertiefung Banking & Finance Law (LL.M.) abgeschlossen. Michael Kummer ist als Gastreferent im Executive MBA Programm „Business Law for International Managers" der Universität St.Gallen (HSG) tätig.'
      ],
      externalLink: null
    },
    {
      id: 3,
      name: 'Beni Gmür',
      position: 'Leiter Hauswartung',
      subtitle: '',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/kc4b6z81_Beni%20Gm%C3%BCr.webp',
      bio: [],
      externalLink: null
    },
    {
      id: 4,
      name: 'Stefanie Scherzinger',
      position: 'Sekretariat',
      subtitle: 'Leiterin Backoffice',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/8iv5k95c_Stefanie%20Scherzinger.webp',
      bio: [],
      externalLink: null
    },
    {
      id: 5,
      name: 'Nadja Rindlisbacher',
      position: 'Leiterin Verwaltung',
      subtitle: '',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/ntcru82x_Nadja%20Rindlisbacher.webp',
      bio: [],
      externalLink: null
    }
  ];

  const stats = [
    { value: 50, prefix: '', suffix: '+', label: 'Projekte', icon: Building2, description: 'erfolgreich realisiert' },
    { value: 15, prefix: '', suffix: '+', label: 'Jahre Expertise', icon: Award, description: 'in der Immobilienbranche' },
    { value: 65, prefix: '', suffix: ' Mio.', label: 'Auftragsvolumen', icon: TrendingUp, description: 'umgesetzt' },
    { value: 100, prefix: '', suffix: '%', label: 'Ostschweiz', icon: MapPin, description: 'regional verankert' }
  ];

  const handleMemberClick = (member) => {
    setSelectedMember(selectedMember?.id === member.id ? null : member);
  };

  useSEO('about');

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Floating geometric shapes - visible throughout the page */}
      <ParallaxShapes scrollY={scrollY} />
      
      {/* Scroll progress indicator */}
      <ScrollProgress />
      
      {/* Content Container */}
      <div className="relative" style={{ zIndex: 2 }}>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-[#264F77] text-white relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{ transform: `translateY(${scrollY * 0.2}px)` }}
          >
            <div className="absolute top-20 left-10 w-64 h-64 border border-white/30 rounded-full" />
            <div className="absolute top-40 right-20 w-32 h-32 border border-white/20 rotate-45" />
            <div className="absolute bottom-10 left-1/3 w-48 h-48 border border-white/20 rounded-full" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInOnScroll direction="up">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-5xl md:text-6xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Über LIVAG AG
                </h1>
                <p className="text-xl text-gray-200 leading-relaxed">
                  Die LIVAG AG ist spezialisiert auf Projektentwicklung in der Ostschweiz. Mit Weitblick, Qualität und finanzieller Klarheit begleiten wir Immobilien von der Idee bis zur Realisierung.
                </p>
              </div>
            </FadeInOnScroll>
          </div>
        </section>

        {/* Modern Trust Stats Section */}
        <section className="py-20 bg-white/80 backdrop-blur-sm relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute -top-20 -right-20 w-96 h-96 bg-[#264F77]/5 rounded-full blur-3xl"
              style={{ transform: `translate(${scrollY * 0.05}px, ${scrollY * 0.02}px)` }}
            />
            <div 
              className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#264F77]/5 rounded-full blur-3xl"
              style={{ transform: `translate(${-scrollY * 0.03}px, ${-scrollY * 0.02}px)` }}
            />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInOnScroll direction="up">
              <h2 className="text-3xl md:text-4xl font-normal text-[#264F77] text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Unsere Erfolgsbilanz
              </h2>
              <p className="text-gray-600 text-center mb-16 max-w-2xl mx-auto">
                Zahlen, die für sich sprechen
              </p>
            </FadeInOnScroll>
            
            <StaggerChildren className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" staggerDelay={0.15}>
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div 
                    key={index} 
                    className="group relative bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden"
                    style={{ 
                      transform: `translateY(${Math.sin(scrollY * 0.008 + index) * 8}px)`,
                      transition: 'transform 0.4s ease-out, box-shadow 0.3s ease'
                    }}
                  >
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#264F77] to-[#1e3d5c] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#264F77]/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 sm:mb-5 transition-colors duration-500">
                        <Icon className="text-[#264F77] group-hover:text-white transition-colors duration-500" size={24} />
                      </div>
                      <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#264F77] group-hover:text-white mb-2 transition-colors duration-500">
                        <AnimatedCounter 
                          end={stat.value} 
                          prefix={stat.prefix} 
                          suffix={stat.suffix}
                          duration={2500}
                        />
                      </div>
                      <p className="text-[#264F77] group-hover:text-white font-semibold text-base sm:text-lg mb-1 transition-colors duration-500">
                        {stat.label}
                      </p>
                      <p className="text-gray-500 group-hover:text-white/70 text-xs sm:text-sm transition-colors duration-500">
                        {stat.description}
                      </p>
                    </div>
                    
                    {/* Decorative corner - hidden on mobile */}
                    <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-[#264F77]/5 group-hover:bg-white/10 rounded-full transition-colors duration-500 hidden sm:block" />
                  </div>
                );
              })}
            </StaggerChildren>
          </div>
        </section>

        {/* Team Grid Section */}
        <section className="py-24 bg-white/70 backdrop-blur-sm relative">
          {/* Parallax decorative elements */}
          <div 
            className="absolute top-0 right-0 w-96 h-96 bg-[#264F77]/5 rounded-full blur-3xl"
            style={{ transform: `translate(${scrollY * 0.05}px, ${-scrollY * 0.1}px)` }}
          />
          <div 
            className="absolute bottom-0 left-0 w-64 h-64 bg-[#264F77]/5 rounded-full blur-3xl"
            style={{ transform: `translate(${-scrollY * 0.05}px, ${scrollY * 0.1}px)` }}
          />
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInOnScroll direction="up">
              <h2 className="text-4xl md:text-5xl font-normal text-[#264F77] text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Unser Team
              </h2>
              <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto text-lg leading-relaxed">
                Hinter jedem erfolgreichen Projekt stehen engagierte Menschen. Lernen Sie die Köpfe kennen, die mit Leidenschaft und Expertise Ihre Immobilienvisionen verwirklichen.
              </p>
            </FadeInOnScroll>
            
            {/* Team Grid with 3D hover effect - 3+2 centered layout */}
            <div className="mb-16 pt-12">
              {/* First row - 3 members */}
              <StaggerChildren className="flex flex-wrap justify-center gap-8 mb-8" staggerDelay={0.1}>
                {teamMembers.slice(0, 3).map((member, index) => (
                  <div
                    key={member.id}
                    onClick={() => member.bio.length > 0 ? handleMemberClick(member) : null}
                    className={`team-card relative rounded-xl transition-all duration-500 ${
                      member.bio.length > 0 ? 'cursor-pointer' : ''
                    } ${
                      selectedMember?.id === member.id ? 'ring-4 ring-[#264F77] ring-offset-4' : ''
                    }`}
                    style={{
                      perspective: '1000px',
                      transformStyle: 'preserve-3d',
                      width: '200px'
                    }}
                  >
                    {/* Card inner with 3D effect */}
                    <div className="team-card-inner relative group">
                      {/* Image Container - fixed size for all cards */}
                      <div 
                        className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-xl shadow-lg transition-all duration-500 relative overflow-visible group-hover:shadow-2xl"
                        style={{ width: '200px', height: '260px' }}
                      >
                        {/* Image that pops out on hover */}
                        <img
                          src={member.image}
                          alt={`${member.name} - ${member.position} bei LIVAG AG St. Gallen | Immobilienexperte Ostschweiz`}
                          title={`${member.name} - ${member.position} | LIVAG Team`}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover object-top rounded-xl transition-all duration-500 ease-out group-hover:scale-[1.15] group-hover:-translate-y-6 group-hover:shadow-2xl group-hover:z-[100]"
                          style={{
                            transformOrigin: 'center bottom'
                          }}
                        />
                        
                        {/* Frame border that stays in place */}
                        <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-[#264F77]/30 transition-all duration-500 pointer-events-none z-[5]" />
                        
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-xl z-[15]" />
                      </div>
                      
                      {/* Name below - always visible with proper spacing */}
                      <div className="mt-4 text-center px-2">
                        <h3 className="text-[#264F77] font-semibold text-base leading-tight">
                          {member.name}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1 leading-tight">
                          {member.position}
                        </p>
                        {member.subtitle && (
                          <p className="text-gray-500 text-xs mt-0.5">
                            {member.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </StaggerChildren>
              
              {/* Second row - 2 members centered */}
              <StaggerChildren className="flex flex-wrap justify-center gap-8" staggerDelay={0.1}>
                {teamMembers.slice(3).map((member, index) => (
                <div
                  key={member.id}
                  onClick={() => member.bio.length > 0 ? handleMemberClick(member) : null}
                  className={`team-card relative rounded-xl transition-all duration-500 ${
                    member.bio.length > 0 ? 'cursor-pointer' : ''
                  } ${
                    selectedMember?.id === member.id ? 'ring-4 ring-[#264F77] ring-offset-4' : ''
                  }`}
                  style={{
                    perspective: '1000px',
                    transformStyle: 'preserve-3d',
                    width: '200px'
                  }}
                >
                  {/* Card inner with 3D effect */}
                  <div className="team-card-inner relative group">
                    {/* Image Container - fixed size for all cards */}
                    <div 
                      className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-xl shadow-lg transition-all duration-500 relative overflow-visible group-hover:shadow-2xl"
                      style={{ width: '200px', height: '260px' }}
                    >
                      {/* Image that pops out on hover */}
                      <img
                        src={member.image}
                        alt={`${member.name} - ${member.position} bei LIVAG AG St. Gallen | Immobilienexperte Ostschweiz`}
                        title={`${member.name} - ${member.position} | LIVAG Team`}
                        loading="lazy"
                        width="200"
                        height="260"
                        className="absolute inset-0 w-full h-full object-cover object-top rounded-xl transition-all duration-500 ease-out group-hover:scale-[1.15] group-hover:-translate-y-6 group-hover:shadow-2xl group-hover:z-[100]"
                        style={{
                          transformOrigin: 'center bottom'
                        }}
                      />
                      
                      {/* Frame border that stays in place */}
                      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-[#264F77]/30 transition-all duration-500 pointer-events-none z-[5]" />
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-xl z-[15]" />
                    </div>
                    
                    {/* Name below - always visible with proper spacing */}
                    <div className="mt-4 text-center px-2">
                      <h3 className="text-[#264F77] font-semibold text-base leading-tight">
                        {member.name}
                      </h3>
                      <p className="text-gray-600 text-sm mt-1 leading-tight">
                        {member.position}
                      </p>
                      {member.subtitle && (
                        <p className="text-gray-500 text-xs mt-0.5">
                          {member.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </StaggerChildren>
            </div>

            {/* Selected Member Detail Section */}
            {selectedMember && selectedMember.bio.length > 0 && (
              <div className="bg-gradient-to-br from-[#F8F9FA] to-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 animate-fadeIn relative">
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Small image on the left */}
                  <div className="md:w-48 flex-shrink-0">
                    <img
                      src={selectedMember.image}
                      alt={`${selectedMember.name} - ${selectedMember.position} bei LIVAG AG | Immobilienberatung St. Gallen`}
                      title={`${selectedMember.name} - Kontaktieren Sie uns für Haus kaufen oder verkaufen`}
                      loading="lazy"
                      className="w-32 h-32 md:w-48 md:h-48 object-cover object-top rounded-xl shadow-lg mx-auto"
                    />
                  </div>
                  
                  {/* Content on the right */}
                  <div className="flex-1">
                    <h3 className="text-3xl font-normal text-[#264F77] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {selectedMember.name}
                    </h3>
                    <p className="text-[#264F77] font-medium mb-1">
                      {selectedMember.position}
                    </p>
                    <p className="text-gray-500 text-sm mb-6">
                      {selectedMember.subtitle}
                    </p>
                    
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      {selectedMember.bio.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                    
                    {selectedMember.externalLink && (
                      <div className="mt-8">
                        <a
                          href={selectedMember.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-6 py-3 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-all duration-300 shadow-md hover:shadow-lg min-h-[48px]"
                          aria-label={`Mehr über ${selectedMember.name} erfahren`}
                        >
                          Profil von {selectedMember.name} ansehen
                          <ExternalLink className="ml-2" size={18} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Close button */}
                <button
                  onClick={() => setSelectedMember(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-gray-50/80 backdrop-blur-sm relative overflow-hidden">
          <div 
            className="absolute top-0 left-0 w-96 h-96 bg-[#264F77]/5 rounded-full blur-3xl"
            style={{ transform: `translate(${-scrollY * 0.03}px, ${scrollY * 0.05}px)` }}
          />
          <div 
            className="absolute bottom-0 right-0 w-80 h-80 bg-[#264F77]/5 rounded-full blur-3xl"
            style={{ transform: `translate(${scrollY * 0.03}px, ${-scrollY * 0.05}px)` }}
          />
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInOnScroll direction="up">
              <h2 className="text-4xl md:text-5xl font-normal text-[#264F77] text-center mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Unsere Werte & Grundsätze
              </h2>
              <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto text-lg">
                Was uns antreibt und wie wir arbeiten
              </p>
            </FadeInOnScroll>
            
            <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerDelay={0.15}>
              {/* Nachhaltig */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 text-center group">
                <div className="w-20 h-20 bg-[#264F77]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#264F77] transition-colors duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#264F77] group-hover:text-white transition-colors duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-normal text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Nachhaltig
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Wir denken langfristig und handeln verantwortungsvoll. Nachhaltigkeit ist für uns kein Trend, sondern ein Grundprinzip – in der Planung, Umsetzung und im Betrieb unserer Immobilienprojekte.
                </p>
              </div>

              {/* Erfolgsorientiert */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 text-center group">
                <div className="w-20 h-20 bg-[#264F77]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#264F77] transition-colors duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#264F77] group-hover:text-white transition-colors duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-2xl font-normal text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Erfolgsorientiert
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Ihr Erfolg ist unser Massstab. Wir setzen auf messbare Ergebnisse, optimierte Renditen und wirtschaftlich tragfähige Lösungen, die Mehrwert schaffen – für Sie und Ihre Investitionen.
                </p>
              </div>

              {/* Partnerschaftlich */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 text-center group">
                <div className="w-20 h-20 bg-[#264F77]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#264F77] transition-colors duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#264F77] group-hover:text-white transition-colors duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-normal text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Partnerschaftlich
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Zusammenarbeit auf Augenhöhe. Wir pflegen langfristige Beziehungen zu unseren Kunden, Partnern und Lieferanten – geprägt von Vertrauen, Transparenz und gegenseitigem Respekt.
                </p>
              </div>
            </StaggerChildren>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-[#264F77] text-white relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{ transform: `translateY(${scrollY * 0.15}px)` }}
          >
            <div className="absolute top-10 left-1/4 w-40 h-40 border border-white/30 rounded-full" />
            <div className="absolute bottom-20 right-1/4 w-60 h-60 border border-white/20 rounded-full" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <FadeInOnScroll direction="up">
              <h2 className="text-4xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Möchten Sie mehr über uns erfahren?
              </h2>
              <p className="text-xl mb-8 text-gray-200 max-w-2xl mx-auto">
                Kontaktieren Sie uns für ein persönliches Gespräch.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#264F77] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Jetzt Immobilie bewerten lassen
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </FadeInOnScroll>
          </div>
        </section>
      </div>
    </div>
  );
};
